import { createContext, useContext } from 'react';
import type { ResolvedNode } from '../../../../vendor/agentplace-a2ui/walker.ts';
import type { ComposerItemInput } from '../stage/composer-draft.ts';

/** Per-surface API handed to catalog components: pointer reads/writes are
 *  scoped to this surface's data model; submit validates and stages a request. */
export interface A2uiSurfaceApi {
  readonly surfaceId: string;
  readonly contentId?: string;
  sendIntent(intent: string): void;
  /** Stage content, an intended request or declared input fields in the normal composer.
   * No model call or business action. Never copy the whole UI model or sensitive fields.
   * Available only when this viewer has a composer. Repeated ids replace the staged item. */
  addToMessage?: (item: ComposerItemInput) => void;
  getValue(pointer: string): unknown;
  setValue(pointer: string, value: unknown): void;
  /** Validate a primitive Button's declared action and stage it. Sending stays in the composer. */
  submit(node: ResolvedNode): void;
  /** Dispatch a named action with an already-resolved context (signature
   *  components construct their own events, e.g. tapping a service card).
   *  For immediate conversational replies, not collecting or committing fields. State sync and a new turn carrying
   *  the a2uiAction metadata. Pass the visible choice or action label in the
   *  session language: it becomes the visitor's message and history caption,
   *  while name/context remain machine identity. Omission is supported for
   *  older components and uses a localized generic caption. */
  dispatch(name: string, context: Record<string, unknown>, label?: string): void;
  /**
   * Push what the visitor has typed to the server WITHOUT submitting anything.
   * Called on field blur, so the agent can reason about a form in progress —
   * asked "is this right?", it can see the answer instead of the empty shape it
   * rendered.
   *
   * Blur, not keystroke: a per-character sync is waste, and `<ui_state>` is
   * re-read on every model call. Sensitive kinds are withheld entirely
   * (`isSensitiveFieldKind`), and the dirty overlay is NOT cleared — only a
   * real submit does that.
   */
  commitValue(): void;
  errorsFor(nodeId: string): string[];
}

export const A2uiSurfaceContext = createContext<A2uiSurfaceApi | null>(null);

export function useSurfaceAction(): A2uiSurfaceApi['dispatch'] | null {
  return useContext(A2uiSurfaceContext)?.dispatch ?? null;
}
